/**
 * One food on the trainer's view of a day.
 *
 * Five things distinguish this from the builder's food row, and each is the answer to a question the
 * trainer opens this screen with:
 *
 *   1. **Unticked rows are DIMMED, never hidden.** A food the trainee skipped is the most
 *      interesting row on the card — it is the gap between the plan and the day — and hiding it
 *      would make a half-followed meal look like a short one. This is why the row renders at all.
 *   2. **A source tag**, so trainer food and trainee food never read as one list.
 *   3. **A substituted row names what it replaced** (`במקום חזה עוף`), on its own line: it carries
 *      another food's name and cannot share a line with one.
 *   4. **The trainee's own additions are tinted** and carry a start-edge marker.
 *   5. **An off-plan amount shows the plan struck through**, with the drift beside it — the pair is
 *      what makes "ate 3 instead of 2" legible at a glance.
 *
 * Nothing is editable. The log is the trainee's.
 */

import Box from "@mui/material/Box";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { MacroLine } from "../ui/MacroChips";
import { num } from "../ui/format";
import { deviationValue, energyFigure } from "../ui/nutritionUnits";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";
import { v2 } from "../ui/v2Tokens";
import { servingAmount, type MealRowView } from "../model/dayLogV2Model";
import { EatenMark } from "./EatenMark";

const TAG: Record<MealRowView["source"], string> = {
  planned: "מהתפריט",
  // Not the app's "הוספה שלי" — here the reader is the COACH, and "שלי" would name the wrong person.
  added: "הוספה של המתאמן",
  substituted: "במקום",
};

/**
 * A number beside Hebrew text is reordered by the bidi algorithm unless it is isolated. Every
 * quantity and unit on this row goes through a pair of these rather than being concatenated.
 */
const nums = { unicodeBidi: "isolate", direction: "ltr" as const, display: "inline-block" };

const Amount = ({ value, unit, color }: { value: number; unit: string; color?: string }) => (
  <Box component="span" sx={{ color, display: "inline-flex", gap: 0.4, alignItems: "baseline" }}>
    <Box component="span" sx={nums}>
      {num(value)}
    </Box>
    {unit ? <Box component="span">{unit}</Box> : null}
  </Box>
);

export const LogFoodRow = ({ row }: { row: MealRowView }) => {
  const { item, source, isPlanned, isEaten, actualQty, plannedQty, deviation, nutrition } = row;
  const offPlan = Boolean(deviation && deviation.direction !== "on");
  const driftColor = deviation?.direction === "over" ? v2.over : v2.under;
  const mode = useNutritionDisplayMode();
  const calories = energyFigure(nutrition.calories, mode, { short: true });
  // whole kcal in calories mode (as `num` printed it), one decimal of a point otherwise
  const driftShown = deviation ? deviationValue(deviation.kcal, mode) : 0;

  const shown = servingAmount(actualQty, item.servingName);
  const planned = servingAmount(plannedQty, item.servingName);

  return (
    <Box
      data-testid="log-food-row"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        px: 1.25,
        py: 1,
        borderRadius: "10px",
        // Dim, never hide — see the note at the top of the file.
        opacity: isEaten ? 1 : 0.5,
        ...(isPlanned
          ? {}
          : {
              backgroundColor: v2.brandWash,
              // Logical property: the RTL theme flips it, so this is the RIGHT edge in Hebrew.
              // Never a manual `direction` or a hard-coded left/right here.
              borderInlineStart: `3px solid ${v2.brand}`,
            }),
      }}
    >
      <Box sx={{ pt: 0.25 }}>
        <EatenMark state={isEaten ? "all" : "none"} size={16} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
          <Box sx={{ fontSize: 13.5, fontWeight: 600, color: v2.ink }}>{item.name}</Box>
          {item.brand ? <Box sx={{ fontSize: 11.5, color: v2.ink4 }}>{item.brand}</Box> : null}
          {source !== "substituted" && (
            <Box
              sx={{
                fontSize: 10.5,
                fontWeight: 600,
                px: 0.75,
                py: 0.15,
                borderRadius: "6px",
                color: isPlanned ? v2.ink3 : v2.brandText,
                backgroundColor: isPlanned ? v2.lineSoft : v2.brandWash,
              }}
            >
              {TAG[source]}
            </Box>
          )}
        </Box>

        {source === "substituted" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.35,
              fontSize: 11.5,
              color: v2.warn,
            }}
          >
            <SwapHorizOutlinedIcon sx={{ fontSize: 14 }} />
            <Box component="span">
              {TAG.substituted} {row.originName}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.75,
            mt: 0.35,
            fontSize: 12,
            color: v2.ink3,
            flexWrap: "wrap",
          }}
        >
          <Amount value={shown.value} unit={shown.unit} color={offPlan ? driftColor : undefined} />
          {/* Only a PLANNED row has a plan to have departed from. An addition replaces nothing. */}
          {isPlanned && offPlan && (
            <>
              <Box component="span" sx={{ textDecoration: "line-through", color: v2.ink4 }}>
                מתוכנן <Amount value={planned.value} unit={planned.unit} />
              </Box>
              <Box component="span" sx={{ color: driftColor, fontWeight: 600 }}>
                {deviation!.direction === "over" ? "▲" : "▼"}{" "}
                <Box component="span" sx={nums}>
                  {Math.abs(Math.round(deviation!.pct * 100))}%
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ textAlign: "end", flex: "0 0 auto" }}>
        <Box sx={{ fontSize: 12.5, fontWeight: 700, color: v2.ink2, ...nums }}>
          {`${calories.text} ${calories.unit}`}
        </Box>
        {/* The calorie cost of the drift, and only once the trainee has actually eaten the row —
            a different amount not yet eaten is a plan, not a deviation. */}
        {offPlan && isEaten && (
          <Box sx={{ fontSize: 11, fontWeight: 600, color: driftColor, ...nums }}>
            {driftShown > 0 ? "+" : ""}
            {driftShown.toLocaleString("he-IL", { maximumFractionDigits: 1 })}
          </Box>
        )}
        <Box sx={{ mt: 0.35 }}>
          <MacroLine totals={nutrition} size={10.5} plain />
        </Box>
      </Box>
    </Box>
  );
};
